import { createUniqueId, For, type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { type GateState, gateCode, gateState, type MergeGate } from '../lib/merge-override';
import { count } from '../lib/time';

// The Merge Gate (#6779, prototype `:4068-4073`, `:4144`): one repo's platform-side hold
// (#7011), its button or its badge, the effective policy line and the gate counts. Presenter
// over `createMergeGate` (`lib/merge-override.ts`), which the Core owns so a save in flight
// can freeze the subject.
//
// WHO GETS THE BUTTON: `canWrite`, which the Fleet page computes as `roleSettled &&
// canAdminWrite(role)` (`routes/Fleet.tsx`): admins and owners, since #7011's
// `write:merge:gate` sits in `ADMIN_SCOPES`. A member and a viewer see the state as a badge
// with the same words, and the reason they cannot change it. The server stays the arbiter:
// a credential the role check lets through but the scope does not gets the route's 403,
// shown by the gate's toast, and nothing changes.
//
// "Autonomous" is never offered where `file_allows` is false (ruling G-7): such a repo reads
// "Hold for review" (holding is always available) or, once held, "Held for review" with
// `aria-disabled` (still focusable, so its reason, the tooltip, is read by `aria-describedby`;
// a click sends nothing). The third label and the per-repo subject are writer's defaults, open at owner
// checkpoint (c).

const LABEL: Readonly<Record<GateState, () => string>> = {
  autonomous: () => t('fleet.live.gate_autonomous'),
  held: () => t('fleet.live.gate_held'),
  hold: () => t('fleet.live.gate_hold'),
  locked: () => t('fleet.live.gate_held'),
};

const TITLE: Readonly<Record<GateState, () => string>> = {
  autonomous: () => t('fleet.live.gate_autonomous_tip'),
  held: () => t('fleet.live.gate_held_tip'),
  hold: () => t('fleet.live.gate_hold_tip'),
  locked: () => t('fleet.live.gate_locked_tip'),
};

export type FleetLiveGateProps = {
  readonly gate: MergeGate;
  readonly repo: string;
  readonly canWrite: boolean;
  /** The subject squad's newest merge number, or `null` when the read sent none. */
  readonly lastMerge: number | null;
  /** The subject squad's PRs at the gate, or `null` when the read sent no count. */
  readonly atGate: number | null;
};

export function FleetLiveGate(props: FleetLiveGateProps): JSX.Element {
  const headingId = createUniqueId();
  const tipId = createUniqueId();
  const state = (): GateState | null => {
    const s = props.gate.state();
    return s === undefined ? null : gateState(s);
  };
  const click = (): void => {
    if (state() === 'locked') return;
    void props.gate.save.run();
  };
  return (
    <section class="fleet-live__gate" aria-labelledby={headingId}>
      <div class="fleet-live__gate-head">
        <h3 id={headingId} class="fleet-live__title">
          {t('fleet.live.gate_title', { repo: props.repo })}
        </h3>
        <Show when={state()}>
          {(s) => (
            <Show
              when={props.canWrite}
              fallback={
                <span class={`fleet-live__gate-btn fleet-live__gate-btn--${s()}`}>
                  <i class="fleet-live__gate-mark" aria-hidden="true" />
                  {LABEL[s()]()}
                </span>
              }
            >
              <button
                type="button"
                class={`fleet-live__gate-btn fleet-live__gate-btn--${s()}`}
                aria-pressed={s() === 'autonomous'}
                aria-disabled={s() === 'locked' || props.gate.save.pending()}
                aria-describedby={tipId}
                title={TITLE[s()]()}
                onClick={click}
              >
                <i class="fleet-live__gate-mark" aria-hidden="true" />
                {props.gate.save.pending() ? t('fleet.live.gate_saving') : LABEL[s()]()}
              </button>
              <span id={tipId} class="sr-only">
                {TITLE[s()]()}
              </span>
            </Show>
          )}
        </Show>
      </div>
      <Switch>
        <Match when={props.gate.unread()}>
          <p class="fleet-live__gate-note">{t('fleet.live.gate_unread')}</p>
        </Match>
        <Match when={state() !== null && !props.canWrite}>
          <p class="fleet-live__gate-note">{t('fleet.live.gate_read_only')}</p>
        </Match>
      </Switch>
      <Show when={props.gate.state()}>
        {(o) => (
          <pre class="fleet-live__gate-code">
            <For each={gateCode(o(), props.repo)}>
              {(line, i) => (
                <span class={i() === 0 ? 'fleet-live__gate-comment' : 'fleet-live__gate-line'}>
                  {line}
                </span>
              )}
            </For>
          </pre>
        )}
      </Show>
      <div class="fleet-live__gate-foot">
        <Show when={props.lastMerge}>
          {(n) => <span>{t('fleet.live.gate_last_merge', { number: count(n()) })}</span>}
        </Show>
        <Show when={props.atGate !== null}>
          <span>{t('fleet.live.gate_at_gate', { count: count(props.atGate ?? 0) })}</span>
        </Show>
      </div>
    </section>
  );
}
